import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, Drawer, IconButton, Button } from "@mui/material";
import SecretList from "components/secrets/SecretList";
import { SecretStub } from "protogen/secrets_pb";
import Loading from "components/common/Loading";
import useIsMobile from "components/hooks/useIsMobile";
import Secret from "components/secrets/Secret";
import FlexPage from "components/layout/FlexPage";
import Breadcrumbs from "components/common/Breadcrumbs";
import { ReactComponent as CloseIcon } from "../icons/Close.svg";
import { useListMemberSecrets } from "services/secrets";
import useTitle from "components/hooks/useTitle";
import SearchBar from "components/common/SearchBar";
import CheckedDropDown from "components/common/CheckedDropDown";
import {
  TYPE_FILTER_OPTIONS,
  SORT_OPTIONS,
  DROPDOWN_SX,
} from "components/secrets/Constants";
import { Check, ShieldCheck } from "lucide-react";
import { CurrentUserContext } from "components/context/RequireAuth";
import { planFlagsFromStatus } from "utils/memberFamilyPlan";
import useDigitalSubscriptionUX from "components/hooks/useDigitalSubscriptionUX";
import { secretsVaultIntro } from "common/memberModuleResolver";
import IntegrationsSection from "components/integrations/IntegrationsSection";

const NEW_SECRET_TYPE_MAP: Record<string, string> = {
  creditCard: "credit_card",
  password: "login",
  other: "secret",
};

export default ({
  showIntegrations = false,
}: {
  showIntegrations?: boolean;
}) => {
  useTitle(showIntegrations ? "Access" : "Secrets");
  const pageTitle = showIntegrations ? "Access" : "Secrets";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const currentUser = useContext(CurrentUserContext);
  const { familyStatus, isUnpaidSignup } = planFlagsFromStatus(
    currentUser.familyStatus,
  );
  const isDigitalSubscription = useDigitalSubscriptionUX();
  const { data, request } = useListMemberSecrets();
  const [showSecondPanel, setShowSecondPanel] = useState(false);
  const [selectedSecret, setSelectedSecret] = useState("");
  const [initialSecretType, setInitialSecretType] = useState("");
  const [query, setQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("title_asc");

  useEffect(() => {
    request();
    const newSecretType = searchParams.get("newSecretType");
    if (newSecretType && NEW_SECRET_TYPE_MAP[newSecretType]) {
      setInitialSecretType(NEW_SECRET_TYPE_MAP[newSecretType]);
      setSelectedSecret("");
      setShowSecondPanel(true);
    }
  }, []);

  const handleClickSecret = (secretRef: string) => {
    if (isMobile) {
      navigate(`/secret/${encodeURIComponent(secretRef)}`);
    } else {
      setSelectedSecret(secretRef);
      setShowSecondPanel(true);
    }
  };

  const closeSecondPanel = (doRefresh?: boolean) => {
    setSelectedSecret("");
    setInitialSecretType("");
    setShowSecondPanel(false);
    if (doRefresh) {
      request();
    }
  };

  const onSaveHandler = () => closeSecondPanel(true);
  const onCancelHandler = () => closeSecondPanel();

  const secrets = data?.secrets ?? [];
  const creators = data?.creators ?? [];
  const displayedSecrets = useMemo(() => {
    let result = [...secrets];
    if (query) {
      const lowerQuery = query.toLowerCase();
      result = result.filter((s) => s.name.toLowerCase().includes(lowerQuery));
    }
    if (selectedTypes.length > 0) {
      result = result.filter((s) => selectedTypes.includes(s.valueType));
    }
    result.sort((a, b) => {
      switch (sortBy) {
        case "title_desc":
          return b.name.localeCompare(a.name);
        case "newest":
          return Number(b.creationTimeSec - a.creationTimeSec);
        case "oldest":
          return Number(a.creationTimeSec - b.creationTimeSec);
        default:
          return a.name.localeCompare(b.name);
      }
    });
    return result;
  }, [data?.secrets, query, selectedTypes, sortBy]);

  if (!data) {
    return <Loading />;
  }

  return (
    <FlexPage leftAligned sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <Breadcrumbs
            breadcrumbs={[{ name: "Home", link: "/" }, { name: pageTitle }]}
          />
          <Typography variant="display" sx={{ marginBottom: "24px" }}>
            {pageTitle}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            onClick={() => {
              handleClickSecret("");
            }}
          >
            New secret
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          width: "100%",
          maxWidth: "800px",
          marginTop: "24px",
        }}
      >
        <Box>
          <Typography
            variant="bodySmall"
            sx={{
              color: "#6B6E7B",
            }}
          >
            {secretsVaultIntro({
              familyStatus,
              isDigitalSubscription,
              isUnpaidSignup,
            })}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px 8px 16px",
            borderRadius: "12px",
            backgroundColor: "#FAF9FA",
          }}
        >
          <ShieldCheck size={24} stroke="#8E9598" style={{ flexShrink: 0 }} />
          <Typography variant="bodySmall" color="text.secondary">
            This information has additional safeguards and is encrypted at rest
            for security.
          </Typography>
        </Box>
        {secrets.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                width: "100%",
                gap: "8px",
              }}
            >
              <SearchBar
                fullWidth={isMobile}
                onQuery={setQuery}
                onKeyPress={true}
                sx={{ marginLeft: "0px", ...(isMobile && { width: "100%" }) }}
              />
              <Box
                sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
              >
                <CheckedDropDown
                  sx={DROPDOWN_SX}
                  options={TYPE_FILTER_OPTIONS}
                  radio={true}
                  onChange={(opts) =>
                    setSelectedTypes(
                      opts.filter((o) => o.value !== "all").map((o) => o.value),
                    )
                  }
                  displayValue="Type"
                  noOutline={true}
                />
                <CheckedDropDown
                  sx={DROPDOWN_SX}
                  options={SORT_OPTIONS}
                  closeOnSelect={true}
                  ActiveIcon={Check}
                  onChange={(opts) => {
                    if (opts.length > 0) setSortBy(opts[0].value);
                  }}
                  displayValue="Sort"
                  noOutline={true}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <SecretList
                secrets={displayedSecrets}
                creators={creators}
                onClick={(secret: SecretStub) => {
                  handleClickSecret(secret.ref);
                }}
                emptyMessage="No secrets match your filters."
              />
            </Box>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "48px 24px",
              gap: "16px",
              borderRadius: "12px",
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <ShieldCheck size={40} stroke="#8E9598" />
            <Typography variant="h3">No secrets yet</Typography>
            <Typography variant="bodySmall" color="text.secondary">
              Store passwords, credit cards, and other sensitive information
              securely. Your secrets are encrypted and only accessible by you.
            </Typography>
            <Button variant="outlined" onClick={() => handleClickSecret("")}>
              Add your first secret
            </Button>
          </Box>
        )}
      </Box>

      {showIntegrations && (
        <Box sx={{ width: "100%", maxWidth: "800px", marginTop: "40px" }}>
          <IntegrationsSection />
        </Box>
      )}

      <Drawer
        anchor="right"
        open={showSecondPanel}
        onClose={() => closeSecondPanel()}
        sx={{
          zIndex: 1202,
          "& .MuiDrawer-paper": {
            width: isMobile ? "100%" : "600px",
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            padding: "24px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <Typography variant="h2">
              {selectedSecret ? "Edit Secret" : "New Secret"}
            </Typography>
            <IconButton onClick={() => closeSecondPanel()}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Secret
              secretRef={selectedSecret}
              onSaveHandler={onSaveHandler}
              onCancelHandler={onCancelHandler}
              initialSecretType={initialSecretType}
            />
          </Box>
        </Box>
      </Drawer>
    </FlexPage>
  );
};
